import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { useManagedPeriods } from '../period-goals/config';
import useOrgGoalTree, {
  createOrgGoal, updateOrgGoal, archiveOrgGoal, flattenTree, getAllowedParentTypes,
} from './config';
import OrgGoalTreeNode from './components/OrgGoalTreeNode';
import OrgGoalFormModal from './components/OrgGoalFormModal';
import BulkAssignModal from '../performance-settings/components/BulkAssignModal';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const Container = styled.div``;

const PeriodSelectWrap = styled.div`
  min-width: 280px;
  margin-bottom: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
`;

const EmptyBox = styled.div`
  padding: 26px 18px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

// Skorlama Plan 4, Task 4 (task-4-brief.md) — Kurumsal Hedefler sayfası:
// company/department/team OrgGoal'larının girintili ağaç görünümü (org-chart
// bağımlılığı BİLİNÇLİ OLARAK yok — brief "hafif tut" diyor: basit genişlet/
// daralt + düz liste, bkz. OrgGoalTreeNode.js) + hizalı bireysel hedef özeti.
// Dönem seçici + boş-durum iskeleti TeamGoalsPage'in "team"/"periodEnd"
// sekmeleriyle BİREBİR aynı (useManagedPeriods + performance.goal.selectPeriod/
// noPeriods/selectPeriodFirst — bu üç anahtar zaten OrgGoal'dan BAĞIMSIZ olarak
// TeamGoalsPage'de paylaşılıyordu, yeni anahtar İCAT edilmedi).
export default function OrgGoalsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canWritePm = canWrite('PM');

  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const { periods, periodsFetching } = useManagedPeriods();
  useEffect(() => {
    if (!selectedPeriodId && periods.length > 0) setSelectedPeriodId(periods[0].id);
  }, [periods, selectedPeriodId]);

  const periodOptions = useMemo(() => periods.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  })), [periods, t]);

  const { tree, fetching, refresh } = useOrgGoalTree(selectedPeriodId);

  // Genişlet/daralt: varsayılan TÜM düğümler açık (kapalı-id kümesi boş
  // başlar) — az sayıda düğüm (en fazla 3 seviye) beklendiği için varsayılan
  // tam-açık görünüm daha az tıklama gerektirir; kullanıcı istediği dalı
  // daraltır.
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const toggleExpand = useCallback((id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const [modalState, setModalState] = useState(null); // { ownerType, goal }
  const openCreate = useCallback((ownerType) => setModalState({ ownerType, goal: null }), []);
  const openEdit = useCallback((node) => setModalState({ ownerType: node.ownerType, goal: node }), []);
  const closeModal = useCallback(() => setModalState(null), []);
  const handleSaved = useCallback(() => { closeModal(); refresh(); }, [closeModal, refresh]);

  const [archiveTarget, setArchiveTarget] = useState(null); // node
  // Müşteri önerisi (2026-08-10): kurumsal hedefi seçili çalışanlara indir —
  // BulkAssignModal orgGoal modunda açılır (dönem = sayfanın seçili dönemi).
  const [bulkAssignOrgGoal, setBulkAssignOrgGoal] = useState(null); // node
  const [archiving, setArchiving] = useState(false);
  const handleArchiveConfirm = useCallback(async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    const ok = await archiveOrgGoal(archiveTarget.id, archiveTarget.ownerType);
    setArchiving(false);
    setArchiveTarget(null);
    if (ok) refresh();
  }, [archiveTarget, refresh]);

  const flatNodes = useMemo(() => flattenTree(tree), [tree]);
  const parentCandidates = useMemo(() => {
    if (!modalState) return [];
    const allowedTypes = getAllowedParentTypes(modalState.ownerType);
    if (allowedTypes.length === 0) return [];
    return flatNodes.filter((n) => allowedTypes.includes(n.ownerType)
      && n.status !== 'archived'
      && n.id !== modalState.goal?.id);
  }, [flatNodes, modalState]);

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('performance.orgGoals.title') }]} />

      <PeriodSelectWrap>
        <GenericSelect
          label={t('performance.goal.selectPeriod')}
          value={selectedPeriodId}
          onChange={setSelectedPeriodId}
          options={periodOptions}
          idKey="id"
          labelKey="name"
        />
      </PeriodSelectWrap>

      {!periodsFetching && periods.length === 0 && (
        <Card><EmptyBox>{t('performance.goal.noPeriods')}</EmptyBox></Card>
      )}
      {periods.length > 0 && !selectedPeriodId && (
        <Card><EmptyBox>{t('performance.goal.selectPeriodFirst')}</EmptyBox></Card>
      )}

      {selectedPeriodId && (
        <>
          <Toolbar>
            {/* team hedefi oluştur — HERKESE görünür (self-servis, BE
                sahiplik reddeder — task-4-brief.md). */}
            <Button data-role="new-team-goal-btn" variant="outline" onClick={() => openCreate('team')}>
              + {t('performance.orgGoals.newTeamGoal')}
            </Button>
            {canWritePm && (
              <>
                <Button data-role="new-company-goal-btn" variant="outline" onClick={() => openCreate('company')}>
                  + {t('performance.orgGoals.newCompanyGoal')}
                </Button>
                <Button data-role="new-department-goal-btn" variant="outline" onClick={() => openCreate('department')}>
                  + {t('performance.orgGoals.newDepartmentGoal')}
                </Button>
              </>
            )}
          </Toolbar>

          <Card>
            {fetching ? (
              <EmptyBox>{t('common.loading')}</EmptyBox>
            ) : tree.length === 0 ? (
              <EmptyBox>{t('performance.orgGoals.empty')}</EmptyBox>
            ) : (
              tree.map((node) => (
                <OrgGoalTreeNode
                  key={node.id}
                  node={node}
                  depth={0}
                  canWritePm={canWritePm}
                  collapsedIds={collapsedIds}
                  onToggleExpand={toggleExpand}
                  onEdit={openEdit}
                  onArchive={setArchiveTarget}
                  onAssignEmployees={setBulkAssignOrgGoal}
                />
              ))
            )}
          </Card>
        </>
      )}

      {bulkAssignOrgGoal && (
        <BulkAssignModal
          orgGoal={{ id: bulkAssignOrgGoal.id, title: bulkAssignOrgGoal.title, periodId: selectedPeriodId }}
          onClose={() => setBulkAssignOrgGoal(null)}
        />
      )}

      {modalState && (
        <OrgGoalFormModal
          open
          ownerType={modalState.ownerType}
          goal={modalState.goal}
          periodId={selectedPeriodId}
          parentCandidates={parentCandidates}
          onCreate={createOrgGoal}
          onUpdate={updateOrgGoal}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDialog
        isOpen={!!archiveTarget}
        title={t('performance.orgGoals.archiveConfirmTitle')}
        message={t('performance.orgGoals.archiveConfirmMessage', { title: archiveTarget?.title || '' })}
        onConfirm={handleArchiveConfirm}
        onCancel={() => setArchiveTarget(null)}
        loading={archiving}
      />
    </Container>
  );
}
